import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import {
  Building2,
  RefreshCw,
  Download,
  Calendar,
  TrendingUp,
  Users,
  Search,
  ChevronRight,
  ChevronDown,
  Lock,
  Unlock,
  Building,
  FileText,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calculator,
  Network
} from 'lucide-react';
import { franquiaService, historicoService } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import ProgressModal from '../components/UI/ProgressModal';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import FranquiaHistoricoModal from '../components/UI/FranquiaHistoricoModal';
import { formatPeriod, formatNumber, getCurrentPeriod } from '../utils/formatters';
import { exportarPDF, exportarCSV, exportarXLS } from '../utils/exportUtils';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriod());
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressData, setProgressData] = useState({
    current: 0,
    total: 0,
    sucessos: 0,
    erros: 0,
    currentFranquia: ''
  });
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Estados para confirmação de atualização
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  
  // Estados para confirmação de toggle de status
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  
  // Estados para modal de histórico
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [selectedFranquia, setSelectedFranquia] = useState(null);
  
  // Estado para controlar quais franquias estão expandidas (todas expandidas por padrão)
  // Não precisa mais de estado - sempre mostra empresas
  
  const queryClient = useQueryClient();

  // Buscar dados das franquias
  const { data: franquias, isLoading: loadingFranquias } = useQuery(
    'franquias-dashboard',
    () => franquiaService.getAll(true)
  );

  // Empresas sempre visíveis - não precisa mais de toggle

  // Função unificada para atualizar dashboard
  const handleAtualizarHistorico = () => {
    setShowUpdateConfirm(true);
  };

  const confirmAtualizarHistorico = () => {
    setShowUpdateConfirm(false);
    setShowProgressModal(true);
    setIsComplete(false);
    setHasError(false);
    setErrorMessage('');

    // Usar o método com progresso que agora busca histórico E empresas
    historicoService.popularHistorico((progressData) => {
      setProgressData(progressData);
    })
      .then((result) => {
        setIsComplete(true);
        setProgressData(prev => ({
          ...prev,
          ...result
        }));
        
        // Invalidar cache para forçar atualização
        queryClient.invalidateQueries(['historicos-periodo', selectedPeriod]);
        queryClient.invalidateQueries(['resumo-periodo', selectedPeriod]);
        
        toast.success('Histórico e empresas atualizados com sucesso!');
      })
      .catch((error) => {
        setHasError(true);
        setErrorMessage(error.message || 'Erro ao atualizar histórico');
        toast.error('Erro ao atualizar histórico');
      });
  };

  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
    setIsComplete(false);
    setHasError(false);
    setErrorMessage('');
  };

  // Mutation para toggle do status da franquia
  const toggleStatusMutation = useMutation(
    ({ id, novoStatus }) => franquiaService.toggleStatus(id, novoStatus),
    {
      onSuccess: () => {
        // Invalidar queries para recarregar dados
        queryClient.invalidateQueries(['historicos-periodo', selectedPeriod]);
        queryClient.invalidateQueries('franquias-dashboard');
        toast.success('Status da franquia atualizado com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status da franquia');
      }
    }
  );

  const handleToggleStatus = (id, novoStatus, nome) => {
    setStatusAction({ id, novoStatus, nome });
    setShowStatusConfirm(true);
  };

  const confirmToggleStatus = () => {
    if (statusAction) {
      toggleStatusMutation.mutate({ id: statusAction.id, novoStatus: statusAction.novoStatus });
      setShowStatusConfirm(false);
      setStatusAction(null);
    }
  };

  const handleShowHistorico = (franquiaOrEmpresa) => {
    setSelectedFranquia(franquiaOrEmpresa);
    setShowHistoricoModal(true);
  };

  // Funções de exportação
  const handleExportarPDF = () => {
    if (filteredHistoricos.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }
    
    try {
      exportarPDF(filteredHistoricos, selectedPeriod, resumoPeriodo?.data);
      toast.success('Relatório PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório PDF');
      console.error('Erro ao exportar PDF:', error);
    }
  };

  const handleExportarCSV = () => {
    if (filteredHistoricos.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }
    
    try {
      exportarCSV(filteredHistoricos, selectedPeriod, resumoPeriodo?.data);
      toast.success('Relatório CSV gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório CSV');
      console.error('Erro ao exportar CSV:', error);
    }
  };

  const handleExportarXLS = () => {
    if (filteredHistoricos.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }
    
    try {
      exportarXLS(filteredHistoricos, selectedPeriod, resumoPeriodo?.data);
      toast.success('Relatório Excel gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório Excel');
      console.error('Erro ao exportar XLS:', error);
    }
  };

  // Função para renderizar o status da franquia
  // inativaNoPeriodo indica se a franquia estava inativa no período consultado (linha do tempo)
  const renderStatus = (status, inativaNoPeriodo = false) => {
    // Se está inativa no período consultado, mostrar indicador especial
    if (inativaNoPeriodo) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
          <XCircle className="w-3 h-3 mr-1" />
          Inativa no período
        </span>
      );
    }
    
    switch (status) {
      case 'A':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Liberada
          </span>
        );
      case 'S':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Aviso de Inadimplência
          </span>
        );
      case 'B':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Bloqueada
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Desconhecido
          </span>
        );
    }
  };

  // Buscar períodos disponíveis
  const { data: periodos } = useQuery(
    'periodos-disponiveis',
    historicoService.getPeriodosDisponiveis
  );

  // Verificar se o período atual existe nos dados, senão usar o mais recente
  useEffect(() => {
    if (periodos?.data && Array.isArray(periodos.data)) {
      const currentPeriod = getCurrentPeriod();
      const availablePeriods = periodos.data;
      
      // Se o período atual não existe nos dados, usar o mais recente
      if (!availablePeriods.includes(currentPeriod) && availablePeriods.length > 0) {
        // Os períodos vêm ordenados do mais recente para o mais antigo
        setSelectedPeriod(availablePeriods[0]);
      }
    }
  }, [periodos]);

  // Buscar históricos do período selecionado
  const { data: historicos, isLoading: loadingHistoricos } = useQuery(
    ['historicos-periodo', selectedPeriod],
    () => historicoService.getByPeriodo(selectedPeriod),
    {
      enabled: !!selectedPeriod
    }
  );

  // Buscar resumo do período selecionado
  const { data: resumoPeriodo, isLoading: loadingResumo } = useQuery(
    ['resumo-periodo', selectedPeriod],
    () => historicoService.getResumoByPeriodo(selectedPeriod),
    {
      enabled: !!selectedPeriod
    }
  );

  // Calcular estatísticas
  const franquiasData = Array.isArray(franquias?.data) ? franquias.data : [];
  const historicosData = Array.isArray(historicos?.data) ? historicos.data : [];
  
  // Garantir que os dados das empresas sejam arrays válidos
  const normalizedHistoricosData = useMemo(() => {
    return historicosData.map(historico => ({
      ...historico,
      empresas: Array.isArray(historico.empresas) ? historico.empresas : []
    }));
  }, [historicosData]);
  
  // Filtrar por nome da franquia, nome da empresa ou CNPJ
  let filteredHistoricos;
  
  if (!searchTerm || searchTerm.trim() === '') {
    // Se não há termo de busca, mostrar todos
    filteredHistoricos = [...normalizedHistoricosData].sort((a, b) => a.nome_franquia.localeCompare(b.nome_franquia, 'pt-BR'));
  } else {
    const searchLower = searchTerm.toLowerCase().trim();
    const searchNumbers = searchTerm.replace(/\D/g, ''); // Para busca de CNPJ
    
    filteredHistoricos = normalizedHistoricosData.filter(historico => {
      const franquiaLower = historico.nome_franquia?.toLowerCase() || '';
      
      // Buscar no nome da franquia
      if (franquiaLower.includes(searchLower)) {
        return true;
      }
      
      // Buscar nas empresas (nome ou CNPJ)
      if (historico.empresas && historico.empresas.length > 0) {
        const found = historico.empresas.some(empresa => {
          const empresaLower = empresa.nome_empresa?.toLowerCase() || '';
          
          // Buscar no nome da empresa
          if (empresaLower.includes(searchLower)) {
            return true;
          }
          
          // Buscar no CNPJ (apenas números)
          if (empresa.cnpj && searchNumbers) {
            const cnpjNumbers = empresa.cnpj.replace(/\D/g, '');
            if (cnpjNumbers.includes(searchNumbers)) {
              return true;
            }
          }
          
          return false;
        });
        return found;
      }
      
      return false;
    }).sort((a, b) => a.nome_franquia.localeCompare(b.nome_franquia, 'pt-BR'));
  }
  
  
  // Stats baseados no período selecionado (prioriza resumoPeriodo para respeitar linha do tempo)
  // Usa apenas franquias ATIVAS no período para os cálculos (respeita data_inativacao)
  let totalFranquias, totalFranquiasAtivas, contasMonitoradas, conexoesPictor, totalContas, mediaPorFranquia;

  // PRIORIZAR resumoPeriodo que já filtra por período e considera data_inativacao
  if (resumoPeriodo?.data) {
    // Usar valores das franquias ATIVAS no período
    totalFranquias = Number(resumoPeriodo.data.total_franquias) || 0;
    totalFranquiasAtivas = Number(resumoPeriodo.data.total_franquias_ativas);
    
    // Se total_franquias_ativas não existe ou é null/undefined, usar total_franquias
    if (isNaN(totalFranquiasAtivas) || totalFranquiasAtivas === null) {
      totalFranquiasAtivas = totalFranquias;
    }
    
    // Usar totais apenas das franquias ativas no período
    // IMPORTANTE: Verificar se o valor existe antes de usar fallback
    const wilAtivas = Number(resumoPeriodo.data.total_wil_ativas);
    const wilGeral = Number(resumoPeriodo.data.total_wil_geral);
    const pictorAtivas = Number(resumoPeriodo.data.total_pictor_ativas);
    const pictorGeral = Number(resumoPeriodo.data.total_pictor_geral);
    
    // Usar valores das ativas se existirem (mesmo que sejam 0)
    contasMonitoradas = !isNaN(wilAtivas) ? wilAtivas : (wilGeral || 0);
    conexoesPictor = !isNaN(pictorAtivas) ? pictorAtivas : (pictorGeral || 0);
    
    totalContas = contasMonitoradas + conexoesPictor;
    mediaPorFranquia = totalFranquiasAtivas > 0 ? Math.round(totalContas / totalFranquiasAtivas) : 0;
    
    // Usar totalFranquiasAtivas como o número principal exibido
    totalFranquias = totalFranquiasAtivas;
  } else if (filteredHistoricos.length > 0) {
    // Fallback para dados dos históricos filtrados
    const historicosAtivos = filteredHistoricos.filter(h => h.inativa_no_periodo !== 1);
    totalFranquias = historicosAtivos.length;
    totalFranquiasAtivas = totalFranquias;
    contasMonitoradas = historicosAtivos.reduce((acc, h) => acc + (h.total_wil || 0), 0);
    conexoesPictor = historicosAtivos.reduce((acc, h) => acc + (h.total_pictor || 0), 0);
    totalContas = contasMonitoradas + conexoesPictor;
    mediaPorFranquia = totalFranquiasAtivas > 0 ? Math.round(totalContas / totalFranquiasAtivas) : 0;
  } else {
    // Valores zerados enquanto carrega
    totalFranquias = 0;
    totalFranquiasAtivas = 0;
    contasMonitoradas = 0;
    conexoesPictor = 0;
    totalContas = 0;
    mediaPorFranquia = 0;
  }

  const stats = [
    {
      name: 'Total de Franquias',
      value: totalFranquias,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      name: 'Contas Monitoradas',
      value: formatNumber(contasMonitoradas),
      icon: Calculator,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      name: 'Média por Franquia',
      value: formatNumber(mediaPorFranquia),
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      name: 'Conexões Pictor',
      value: formatNumber(conexoesPictor),
      icon: Network,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  if (loadingFranquias) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Histórico de contas das franquias
          </p>
        </div>
        
        {/* Filtros */}
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Selecione um período</option>
            {Array.isArray(periodos?.data) && periodos.data.map(periodo => (
              <option key={periodo} value={periodo}>
                {formatPeriod(periodo)}
              </option>
            ))}
          </select>
          
          <button
            onClick={handleAtualizarHistorico}
            disabled={showProgressModal && !isComplete && !hasError}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${showProgressModal && !isComplete && !hasError ? 'animate-spin' : ''}`} />
            {showProgressModal && !isComplete && !hasError ? 'Atualizando...' : 'Atualizar'}
          </button>

          {/* Botões de Exportação */}
          {selectedPeriod && (
            <>
              <div className="border-l border-gray-300 mx-1"></div>
              
              <button
                onClick={handleExportarPDF}
                disabled={loadingHistoricos || filteredHistoricos.length === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Exportar dados completos em PDF"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </button>
              
              <button
                onClick={handleExportarCSV}
                disabled={loadingHistoricos || filteredHistoricos.length === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Exportar dados completos em CSV (colunas separadas)"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </button>
              
              <button
                onClick={handleExportarXLS}
                disabled={loadingHistoricos || filteredHistoricos.length === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Exportar dados completos em Excel (XLSX)"
              >
                <FileText className="w-4 h-4 mr-2" />
                Excel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white overflow-hidden shadow-lg rounded-xl">
              <div className="p-8">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-8 w-8 ${stat.color}`} />
                  </div>
                  <div className="ml-6 w-0 flex-1">
                    <dl>
                      <dt className="text-base font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-3xl font-bold text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Conteúdo baseado no período selecionado */}
      {!selectedPeriod ? (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Carregando período atual...</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aguarde enquanto carregamos os dados do período atual.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Busca */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Buscar franquia, empresa ou CNPJ..."
                />
              </div>
            </div>
          </div>

          {/* Tabela de Históricos */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Histórico do Período: {formatPeriod(selectedPeriod)}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {filteredHistoricos.length} franquia(s) encontrada(s). Clique para expandir e ver empresas.
              </p>
            </div>
            
            <div className="overflow-x-auto">
              {loadingHistoricos ? (
                <div className="flex items-center justify-center h-64">
                  <LoadingSpinner />
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Franquia
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        WIL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pictor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Última Atualização
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredHistoricos.map((historico, index) => {
                      const empresasDaFranquia = (historico.empresas || [])
                        .sort((a, b) => a.nome_empresa.localeCompare(b.nome_empresa, 'pt-BR'));
                      const hasEmpresas = empresasDaFranquia.length > 0;
                      
                      return (
                        <React.Fragment key={`${historico.id_franquia}-${index}`}>
                          {/* Linha da Franquia */}
                          <tr className={`hover:bg-gray-50 ${historico.inativa_no_periodo === 1 ? 'opacity-60 bg-gray-50' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Building2 className="h-5 w-5 text-blue-600" />
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {historico.nome_franquia}
                                  </div>
                                  {hasEmpresas && (
                                    <div className="text-xs text-gray-500">
                                      {empresasDaFranquia.length} empresa(s)
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {renderStatus(historico.status_franquia, historico.inativa_no_periodo === 1)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatNumber(historico.total_wil || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatNumber(historico.total_pictor || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatNumber((historico.total_wil || 0) + (historico.total_pictor || 0))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {historico.datahora_modificacao 
                                ? new Date(historico.datahora_modificacao).toLocaleDateString('pt-BR')
                                : '-'
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleShowHistorico(historico)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  title="Ver histórico de 12 meses"
                                >
                                  <BarChart3 className="w-3 h-3 mr-1" />
                                  Gráfico
                                </button>
                                {historico.status_franquia === 'A' ? (
                                  <>
                                    <button
                                      onClick={() => handleToggleStatus(historico.id_franquia, 'S', historico.nome_franquia)}
                                      disabled={toggleStatusMutation.isLoading}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                                      title="Colocar em aviso de inadimplência"
                                    >
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Aviso
                                    </button>
                                    <button
                                      onClick={() => handleToggleStatus(historico.id_franquia, 'B', historico.nome_franquia)}
                                      disabled={toggleStatusMutation.isLoading}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                      title="Bloquear franquia"
                                    >
                                      <Lock className="w-3 h-3 mr-1" />
                                      Bloquear
                                    </button>
                                  </>
                                ) : historico.status_franquia === 'S' ? (
                                  <>
                                    <button
                                      onClick={() => handleToggleStatus(historico.id_franquia, 'B', historico.nome_franquia)}
                                      disabled={toggleStatusMutation.isLoading}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                      title="Bloquear franquia"
                                    >
                                      <Lock className="w-3 h-3 mr-1" />
                                      Bloquear
                                    </button>
                                    <button
                                      onClick={() => handleToggleStatus(historico.id_franquia, 'A', historico.nome_franquia)}
                                      disabled={toggleStatusMutation.isLoading}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                      title="Liberar franquia"
                                    >
                                      <Unlock className="w-3 h-3 mr-1" />
                                      Liberar
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => handleToggleStatus(historico.id_franquia, 'A', historico.nome_franquia)}
                                    disabled={toggleStatusMutation.isLoading}
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                    title="Liberar franquia"
                                  >
                                    <Unlock className="w-3 h-3 mr-1" />
                                    Liberar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Linhas das Empresas (sempre visíveis) */}
                          {empresasDaFranquia.map((empresa, empIndex) => (
                            <tr key={`${historico.id_franquia}-emp-${empIndex}`} className="bg-gray-50">
                              <td className="px-6 py-3 whitespace-nowrap pl-16">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-6 w-6">
                                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                                      <Building className="h-3 w-3 text-green-600" />
                                    </div>
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm text-gray-900">{empresa.nome_empresa}</div>
                                    <div className="text-xs text-gray-500">{empresa.cnpj || 'Sem CNPJ'}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Empresa
                                </span>
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                {formatNumber(empresa.total_locais || 0)}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                {formatNumber(empresa.total_cftv || 0)}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                {formatNumber((empresa.total_locais || 0) + (empresa.total_cftv || 0))}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                -
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => handleShowHistorico({
                                    ...empresa,
                                    nome_franquia: empresa.nome_empresa,
                                    id_franquia: historico.id_franquia,
                                    ex_empresa: empresa.ex_empresa,
                                    isEmpresa: true
                                  })}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  title="Ver histórico de 12 meses"
                                >
                                  <BarChart3 className="w-3 h-3 mr-1" />
                                  Gráfico
                                </button>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
              
              {!loadingHistoricos && filteredHistoricos.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum histórico encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Não há dados para o período selecionado.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Progress Modal */}
      <ProgressModal
        isOpen={showProgressModal}
        onClose={handleCloseProgressModal}
        progress={progressData}
        isComplete={isComplete}
        hasError={hasError}
        errorMessage={errorMessage}
        title="Atualizando Histórico das Franquias"
        canClose={isComplete || hasError}
      />

      {/* Confirm Update Dialog */}
      <ConfirmDialog
        isOpen={showUpdateConfirm}
        onClose={() => setShowUpdateConfirm(false)}
        onConfirm={confirmAtualizarHistorico}
        title="Atualizar Histórico"
        message="Deseja atualizar o histórico de todas as franquias? Este processo pode levar alguns minutos e irá buscar os dados mais recentes de cada franquia."
        confirmText="Sim, Atualizar"
        cancelText="Cancelar"
        type="info"
      />

      {/* Confirm Status Toggle Dialog */}
      {statusAction && (
        <ConfirmDialog
          isOpen={showStatusConfirm}
          onClose={() => {
            setShowStatusConfirm(false);
            setStatusAction(null);
          }}
          onConfirm={confirmToggleStatus}
          title={statusAction.novoStatus === 'B' ? 'Bloquear Franquia' : 
                statusAction.novoStatus === 'S' ? 'Aviso de Inadimplência' : 'Liberar Franquia'}
          message={
            statusAction.novoStatus === 'B' 
              ? `Tem certeza que deseja bloquear a franquia "${statusAction.nome}"? Ela não poderá mais acessar o sistema até ser reativada.`
              : statusAction.novoStatus === 'S'
              ? `Tem certeza que deseja colocar a franquia "${statusAction.nome}" em aviso de inadimplência? Ela receberá um alerta sobre o status.`
              : `Tem certeza que deseja liberar a franquia "${statusAction.nome}"? Ela voltará a ter acesso normal ao sistema.`
          }
          confirmText={statusAction.novoStatus === 'B' ? 'Sim, Bloquear' : 
                    statusAction.novoStatus === 'S' ? 'Sim, Avisar' : 'Sim, Liberar'}
          cancelText="Cancelar"
          type={statusAction.novoStatus === 'B' ? 'danger' : 
               statusAction.novoStatus === 'S' ? 'warning' : 'success'}
          isLoading={toggleStatusMutation.isLoading}
        />
      )}

      {/* Modal de Histórico */}
      <FranquiaHistoricoModal
        isOpen={showHistoricoModal}
        onClose={() => {
          setShowHistoricoModal(false);
          setSelectedFranquia(null);
        }}
        franquia={selectedFranquia}
        currentPeriod={selectedPeriod}
      />
    </div>
  );
};

export default Dashboard;
